// 파일 전송(설계서 §9.4) — "file" DataChannel 규약의 **순수 로직**(브라우저 API 없음 → Node 로 시험한다).
//
// 🔴 에이전트 `poc/rtc-core-rust/src/file_xfer.rs` · Android `FileProtocol.kt` 와 **같은 규약**이다.
//    상수·메시지 이름·오류 코드·경로 규칙을 바꿀 때는 세 곳을 함께 바꾼다.
//
// 제어는 JSON 텍스트(`t`), 내용은 바이너리 조각: [u32 x][u32 i][u64 offset][payload ≤ CHUNK_BYTES] (리틀엔디언).

export const FILE_CHANNEL_LABEL = 'file';
export const FILE_PROTOCOL_VERSION = 1;
/** 파일당 상한 — 사용자 결정(2026-09-15) 10 GiB. */
export const MAX_FILE_BYTES = 10 * 1024 ** 3;
export const MAX_ENTRIES = 10_000;
export const MAX_DEPTH = 32;
export const MAX_REL_PATH_CHARS = 240;
export const MAX_NAME_CHARS = 255;
/** 파일 패널이 한 폴더에서 받는 항목 상한(넘으면 에이전트가 잘라 주고 `truncated` 로 알린다). */
export const MAX_LIST_ITEMS = 5_000;
/** 파일 패널에서 한 번에 지울 수 있는 항목 수. */
export const MAX_DELETE_NAMES = 1_000;
/** 조각 하나의 내용 바이트(머리 16B 를 더해도 에이전트 수신 버퍼 64KB 안). */
export const CHUNK_BYTES = 32 * 1024;
export const FRAME_HEADER_BYTES = 16;
/** 받는 쪽이 진행(=이어받기 기준)을 알리는 간격. */
export const PROGRESS_EVERY_BYTES = 8 * 1024 * 1024;
/**
 * 받기 창 — 받는 쪽(뷰어)이 `accept.win` 으로 알리면 보내는 쪽(에이전트)은 `prog` 로 확인된 위치보다 이만큼 이상 앞서 보내지 않는다.
 * 🔴 에이전트 `rs_files::RECV_WINDOW_BYTES` 와 같게 · `PROGRESS_EVERY_BYTES` 의 2배 이상.
 * 왜: 저장이 네트워크보다 느리면(통합앱 IPC ~14 MB/s) 받은 조각이 JS 에 무한정 쌓여 화면이 굳었다(2026-09-15 앱 실기 A10).
 */
export const RECV_WINDOW_BYTES = 32 * 1024 * 1024;

/** 메시지 종류(`t`). 에이전트 `msg` 모듈과 같아야 한다. */
export const FILE_MSG = {
  HELLO: 'hello',
  OFFER: 'offer',
  ACCEPT: 'accept',
  REJECT: 'reject',
  FEND: 'fend',
  DONE: 'done',
  PROG: 'prog',
  RESULT: 'result',
  CANCEL: 'cancel',
  PULL: 'pull',
  DRAGOUT: 'dragout',
  LIST: 'list',
  LISTING: 'listing',
  /** 파일 패널 — 새 폴더(`mkdir {x, dir, name}`). */
  MKDIR: 'mkdir',
  /** 파일 패널 — 삭제(`delete {x, dir, names}`). */
  DELETE: 'delete',
  /** 파일 패널 — 내려받기 요청(`get {x, dir, names}` → 에이전트가 `offer {dest:'save'}` 로 답한다). */
  GET: 'get',
  /** 패널 명령이 끝났다(`ok {x}`) — 실패는 `reject {x, err}`. */
  OK: 'ok',
} as const;

/** 실패 사유 코드. 앞 묶음은 에이전트와 같고, 뒤 묶음은 뷰어에서만 생긴다. */
export const FILE_ERR = {
  TOO_LARGE: 'too_large',
  TOO_MANY: 'too_many',
  DISK_FULL: 'disk_full',
  BAD_PATH: 'bad_path',
  CONTROL_OFF: 'control_off',
  NO_USER: 'no_user',
  DENIED: 'denied',
  HASH_MISMATCH: 'hash_mismatch',
  BUSY: 'busy',
  CANCELED: 'canceled',
  PROTOCOL: 'protocol',
  FAILED: 'failed',
  /** pull(Ctrl+C) — 복사할 것이 없었다(조용히 넘어간다). */
  UNCHANGED: 'unchanged',
  /** pull(Ctrl+C) — 복사한 것이 파일이 아니다 → 클립보드 채널(텍스트·이미지)로 가져간다. */
  NOT_FILES: 'not_files',
  /** 파일 패널 — 만들려는 이름이 이미 있다(덮어쓰지 않는다). */
  EXISTS: 'exists',
  /** 파일 패널 — 지우거나 열려던 것이 없다. */
  NOT_FOUND: 'not_found',
  // 뷰어 전용
  TIMEOUT: 'timeout',
  CLOSED: 'closed',
  LOCAL_READ: 'local_read',
  LOCAL_WRITE: 'local_write',
  FOLDER_PASTE: 'folder_paste',
  NO_SAVE_API: 'no_save_api',
  UNSUPPORTED: 'unsupported',
} as const;

/** 사용자 문구(🔴 파일 이름을 넣지 않는다 — 알림에는 개수·크기만). */
export const FILE_ERR_MESSAGE: Record<string, string> = {
  [FILE_ERR.TOO_LARGE]: '파일이 너무 큽니다(파일당 10GB까지).',
  [FILE_ERR.TOO_MANY]: '항목이 너무 많습니다(한 번에 1만 개까지).',
  [FILE_ERR.DISK_FULL]: '받는 쪽 디스크 공간이 부족합니다.',
  [FILE_ERR.BAD_PATH]: '보낼 수 없는 이름이나 경로가 있습니다(예약된 이름·특수 문자·너무 깊은 폴더).',
  [FILE_ERR.CONTROL_OFF]: '상대가 원격 입력 제어를 꺼 두어 파일을 주고받을 수 없습니다.',
  [FILE_ERR.NO_USER]: '원격 PC에 로그인한 사용자가 없어 파일을 주고받을 수 없습니다.',
  [FILE_ERR.DENIED]: '권한이 없어 읽거나 쓸 수 없는 위치입니다.',
  [FILE_ERR.HASH_MISMATCH]: '전송 중 내용이 손상되었습니다. 다시 보내 주세요.',
  [FILE_ERR.BUSY]: '다른 전송이 진행 중입니다. 잠시 후 다시 시도하세요.',
  [FILE_ERR.CANCELED]: '전송이 취소되었습니다.',
  [FILE_ERR.PROTOCOL]: '전송 형식 오류가 발생했습니다.',
  [FILE_ERR.FAILED]: '파일 전송에 실패했습니다.',
  [FILE_ERR.UNCHANGED]: '원격에서 복사된 것이 없습니다.',
  [FILE_ERR.NOT_FILES]: '원격에서 복사한 것이 파일이 아닙니다.',
  [FILE_ERR.EXISTS]: '같은 이름이 이미 있습니다.',
  [FILE_ERR.NOT_FOUND]: '그 항목을 찾을 수 없습니다(다른 곳에서 이미 지웠을 수 있습니다).',
  [FILE_ERR.TIMEOUT]: '상대 응답이 없습니다.',
  [FILE_ERR.CLOSED]: '연결이 끊겨 전송을 멈췄습니다. 다시 연결하면 이어서 보낼 수 있습니다.',
  [FILE_ERR.LOCAL_READ]: '내 PC의 파일을 읽지 못했습니다.',
  [FILE_ERR.LOCAL_WRITE]: '내 PC에 저장하지 못했습니다.',
  [FILE_ERR.FOLDER_PASTE]: '브라우저에서는 폴더를 붙여넣을 수 없습니다. 폴더는 끌어다 놓아 주세요.',
  [FILE_ERR.NO_SAVE_API]: '이 브라우저는 폴더에 저장할 수 없습니다. Chrome·Edge 또는 통합앱을 사용하세요.',
  [FILE_ERR.UNSUPPORTED]: '상대 기기가 지원하지 않는 동작입니다.',
};

export class FileError extends Error {
  /** 오류 코드(`FILE_ERR`). 매개변수 속성 대신 필드로 둔다 — Node 타입 제거 실행(시험)이 매개변수 속성을 지원하지 않는다. */
  readonly code: string;
  /** 로그용 상세(🔴 파일 이름을 넣지 않는다). */
  readonly detail?: string;

  constructor(code: string, detail?: string) {
    super(FILE_ERR_MESSAGE[code] ?? detail ?? code);
    this.name = 'FileError';
    this.code = code;
    this.detail = detail;
  }
}

export type FileFeature = 'push' | 'pull' | 'browse' | 'dragout';

/** 에이전트가 hello 로 알린 능력. */
export interface FileCaps {
  v: number;
  maxFile: number;
  maxEntries: number;
  features: FileFeature[];
  /** 파일 패널 시작 폴더(보드 = /sdcard, PC = 사용자 폴더). */
  home?: string;
}

export function parseFileHello(v: Record<string, unknown>): FileCaps | null {
  if (v.t !== FILE_MSG.HELLO || typeof v.v !== 'number') {
    return null;
  }
  const known: FileFeature[] = ['push', 'pull', 'browse', 'dragout'];
  const features = Array.isArray(v.features)
    ? (v.features.filter((f) => known.includes(f as FileFeature)) as FileFeature[])
    : [];
  return {
    v: v.v,
    maxFile: typeof v.maxFile === 'number' ? v.maxFile : MAX_FILE_BYTES,
    maxEntries: typeof v.maxEntries === 'number' ? v.maxEntries : MAX_ENTRIES,
    features,
    home: typeof v.home === 'string' ? v.home : undefined,
  };
}

// ── 파일 패널(설계서 §9.4 "파일 패널") ──────────────────────────────────────

/** 패널 항목 하나(한 폴더 안의 파일·폴더). */
export interface DirItem {
  name: string;
  dir: boolean;
  /** 바이트(폴더는 0). */
  size: number;
  mtime?: number;
}

/** 한 폴더의 목록(`listing`). */
export interface Listing {
  /** 연 폴더의 절대 경로(빈 문자열 = 최상위 = 드라이브·범위 루트 목록). */
  dir: string;
  /** 위로 갈 곳(없으면 null — 범위의 끝). */
  parent: string | null;
  items: DirItem[];
  /** 항목이 상한을 넘어 잘렸다. */
  truncated: boolean;
}

/**
 * 패널 경로 잇기 — 구분자는 **그 기기가 쓰는 것**을 따른다(Windows `\` · 그 밖 `/`).
 * 최상위 목록(`dir` 이 빈 문자열)의 항목은 이름 자체가 절대 경로(드라이브·범위 루트)다.
 */
export function joinPath(dir: string, name: string): string {
  if (!dir) {
    return name;
  }
  const sep = dir.includes('\\') ? '\\' : '/';
  return dir.endsWith(sep) ? `${dir}${name}` : `${dir}${sep}${name}`;
}

/**
 * 받은 `listing` 을 해석한다. 🔴 원격이 준 값이므로 모양을 믿지 않는다 —
 * 형식이 어긋난 항목은 **버리지 않고 전체를 거절**한다(목록이 조용히 비면 사용자가 "빈 폴더" 로 오해한다).
 */
export function parseListing(v: Record<string, unknown>): Listing {
  if (typeof v.dir !== 'string' || !Array.isArray(v.items)) {
    throw new FileError(FILE_ERR.PROTOCOL, 'listing 모양');
  }
  const items: DirItem[] = v.items.map((raw) => {
    const o = raw as Record<string, unknown>;
    if (typeof o.name !== 'string' || !o.name) {
      throw new FileError(FILE_ERR.PROTOCOL, '항목 이름');
    }
    const item: DirItem = { name: o.name, dir: o.dir === true, size: typeof o.size === 'number' ? o.size : 0 };
    if (typeof o.mtime === 'number') {
      item.mtime = o.mtime;
    }
    return item;
  });
  return {
    dir: v.dir,
    parent: typeof v.parent === 'string' ? v.parent : null,
    items,
    truncated: v.truncated === true,
  };
}

/** 전송 항목(파일 또는 폴더). `path` 는 `/` 구분 상대 경로 — 맨 앞이 사용자가 고른 항목의 이름. */
export interface Entry {
  i: number;
  path: string;
  size: number;
  mtime?: number;
  dir: boolean;
}

export type Dest =
  | { kind: 'paste' }
  | { kind: 'drop'; nx: number; ny: number }
  | { kind: 'dir'; dir: string }
  | { kind: 'save' };

export interface Offer {
  x: number;
  entries: Entry[];
  total: number;
  dest: Dest;
}

// ── 바이너리 조각 ─────────────────────────────────────────────────────────

const TWO_32 = 2 ** 32;

/** 머리 + 내용 → 보낼 바이트. offset 은 10 GiB(> 2^32)까지라 상·하위 u32 로 나눠 쓴다. */
export function encodeFrame(x: number, i: number, offset: number, payload: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(FRAME_HEADER_BYTES + payload.length);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, x >>> 0, true);
  dv.setUint32(4, i >>> 0, true);
  dv.setUint32(8, offset % TWO_32, true);
  dv.setUint32(12, Math.floor(offset / TWO_32), true);
  out.set(payload, FRAME_HEADER_BYTES);
  return out;
}

export interface Frame {
  x: number;
  i: number;
  offset: number;
  payload: Uint8Array;
}

/** 받은 바이트 → 조각. 머리가 모자라거나 내용이 상한을 넘으면 null(버린다). */
export function decodeFrame(buf: ArrayBuffer | Uint8Array): Frame | null {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  if (u8.length < FRAME_HEADER_BYTES || u8.length > FRAME_HEADER_BYTES + CHUNK_BYTES) {
    return null;
  }
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  return {
    x: dv.getUint32(0, true),
    i: dv.getUint32(4, true),
    offset: dv.getUint32(8, true) + dv.getUint32(12, true) * TWO_32,
    payload: u8.subarray(FRAME_HEADER_BYTES),
  };
}

// ── 경로 검사(에이전트와 같은 규칙) ─────────────────────────────────────────

const RESERVED_NAMES = new Set([
  'CON', 'PRN', 'AUX', 'NUL',
  ...Array.from({ length: 9 }, (_, k) => `COM${k + 1}`),
  ...Array.from({ length: 9 }, (_, k) => `LPT${k + 1}`),
]);
const FORBIDDEN = /[<>:"\\|?*\u0000-\u001f]/;

/** 이름 하나를 검사한다. 문제가 없으면 null, 있으면 사유. */
export function checkName(c: string): string | null {
  if (c.length === 0) return '빈 이름';
  if (c === '.' || c === '..') return '상위/현재 폴더 표기';
  if ([...c].length > MAX_NAME_CHARS) return '이름이 너무 김';
  if (c.endsWith('.') || c.endsWith(' ')) return '끝이 점/공백';
  if (FORBIDDEN.test(c)) return '쓸 수 없는 문자';
  const stem = c.split('.')[0].trimEnd().toUpperCase();
  if (RESERVED_NAMES.has(stem)) return '예약된 이름';
  return null;
}

/** 상대 경로를 검사해 구성 요소로 나눈다(보내기 전에 막는다 — 받는 쪽도 같은 규칙으로 거절한다). */
export function validateRelPath(p: string): string[] {
  if (!p) throw new FileError(FILE_ERR.BAD_PATH, '빈 경로');
  if (p.startsWith('/')) throw new FileError(FILE_ERR.BAD_PATH, '절대 경로');
  if ([...p].length > MAX_REL_PATH_CHARS) throw new FileError(FILE_ERR.BAD_PATH, '경로가 너무 김');
  const parts = p.split('/');
  if (parts.length > MAX_DEPTH) throw new FileError(FILE_ERR.BAD_PATH, '폴더가 너무 깊음');
  for (const c of parts) {
    const why = checkName(c);
    if (why) throw new FileError(FILE_ERR.BAD_PATH, why);
  }
  return parts;
}

/** 같은 경로 두 번(대소문자 무시) · 파일 아래 항목을 거절한다. */
export function checkConflicts(entries: Entry[]): void {
  const seen = new Set<string>();
  const files = new Set<string>();
  for (const e of entries) {
    const key = e.path.toLowerCase();
    if (seen.has(key)) throw new FileError(FILE_ERR.BAD_PATH, '같은 경로가 두 번');
    seen.add(key);
    if (!e.dir) files.add(key);
  }
  for (const e of entries) {
    const parts = e.path.toLowerCase().split('/');
    let prefix = '';
    for (const c of parts.slice(0, -1)) {
      prefix = prefix ? `${prefix}/${c}` : c;
      if (files.has(prefix)) throw new FileError(FILE_ERR.BAD_PATH, '파일 아래에 항목');
    }
  }
}

/** 보내기 전에 목록 전체를 검사한다(상한·이름·충돌). */
export function checkEntries(entries: Entry[], caps?: Pick<FileCaps, 'maxFile' | 'maxEntries'>): void {
  const maxFile = Math.min(caps?.maxFile ?? MAX_FILE_BYTES, MAX_FILE_BYTES);
  const maxEntries = Math.min(caps?.maxEntries ?? MAX_ENTRIES, MAX_ENTRIES);
  if (entries.length === 0) throw new FileError(FILE_ERR.PROTOCOL, '보낼 항목 없음');
  if (entries.length > maxEntries) throw new FileError(FILE_ERR.TOO_MANY);
  entries.forEach((e, idx) => {
    if (e.i !== idx) throw new FileError(FILE_ERR.PROTOCOL, '순번 불일치');
    validateRelPath(e.path);
    if (e.dir && e.size !== 0) throw new FileError(FILE_ERR.PROTOCOL, '폴더에 크기');
    if (e.size > maxFile) throw new FileError(FILE_ERR.TOO_LARGE);
  });
  checkConflicts(entries);
}

export function totalBytes(entries: Entry[]): number {
  return entries.reduce((s, e) => s + (e.dir ? 0 : e.size), 0);
}

export function makeOffer(x: number, entries: Entry[], dest: Dest): Offer {
  return { x, entries, total: totalBytes(entries), dest };
}

export function offerJson(o: Offer): string {
  return JSON.stringify({ t: FILE_MSG.OFFER, x: o.x, entries: o.entries, total: o.total, dest: o.dest });
}

/** 받은 offer 를 해석·검사한다(에이전트→뷰어 전송). 하나라도 어기면 전체 거절. */
export function parseOffer(v: Record<string, unknown>): Offer {
  const x = v.x;
  if (typeof x !== 'number' || !Number.isInteger(x) || x < 0 || x >= TWO_32) {
    throw new FileError(FILE_ERR.PROTOCOL, 'x 없음');
  }
  if (!Array.isArray(v.entries)) throw new FileError(FILE_ERR.PROTOCOL, 'entries 없음');
  const entries: Entry[] = v.entries.map((raw) => {
    const e = raw as Record<string, unknown>;
    const entry: Entry = {
      // 순번이 없거나 위치와 다르면 checkEntries 가 거절한다(-1 은 어떤 위치와도 같지 않다).
      i: typeof e.i === 'number' ? e.i : -1,
      path: typeof e.path === 'string' ? e.path : '',
      size: typeof e.size === 'number' ? e.size : 0,
      dir: e.dir === true,
    };
    // 수정 시각은 있을 때만 싣는다(없는 키를 undefined 로 만들면 보낸 것과 모양이 달라진다).
    if (typeof e.mtime === 'number') {
      entry.mtime = e.mtime;
    }
    return entry;
  });
  checkEntries(entries);
  const d = (v.dest ?? {}) as Record<string, unknown>;
  let dest: Dest;
  switch (d.kind) {
    case 'paste':
    case 'save':
      dest = { kind: d.kind };
      break;
    case 'drop':
      if (typeof d.nx !== 'number' || typeof d.ny !== 'number' || d.nx < 0 || d.nx > 1 || d.ny < 0 || d.ny > 1) {
        throw new FileError(FILE_ERR.PROTOCOL, 'drop 좌표');
      }
      dest = { kind: 'drop', nx: d.nx, ny: d.ny };
      break;
    case 'dir':
      if (typeof d.dir !== 'string' || !d.dir) throw new FileError(FILE_ERR.PROTOCOL, 'dir 없음');
      dest = { kind: 'dir', dir: d.dir };
      break;
    default:
      throw new FileError(FILE_ERR.PROTOCOL, 'dest 종류');
  }
  return makeOffer(x, entries, dest);
}

/** 전송 id(u32, 0 제외) — 조각 머리에 들어간다. */
export function newTransferId(): number {
  const a = new Uint32Array(1);
  do {
    crypto.getRandomValues(a);
  } while (a[0] === 0);
  return a[0];
}

/** 사람이 읽는 크기(알림·진행 목록). */
export function formatBytes(n: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) {
    v /= 1024;
    u += 1;
  }
  return u === 0 ? `${v} ${units[u]}` : `${v.toFixed(v >= 100 ? 0 : 1)} ${units[u]}`;
}

/** 알림용 요약(🔴 이름 없이): "파일 3개 · 폴더 1개 · 12.3 MB". */
export function describeEntries(entries: Entry[]): string {
  const files = entries.filter((e) => !e.dir).length;
  const dirs = entries.filter((e) => e.dir && !e.path.includes('/')).length;
  const parts = [`파일 ${files}개`];
  if (dirs > 0) parts.push(`폴더 ${dirs}개`);
  parts.push(formatBytes(totalBytes(entries)));
  return parts.join(' · ');
}
